import { filter } from 'ramda';
import { mapDateToUI, mapToExactDate } from '@bridebook/toolbox/src';
import type { IDatePickerUI } from '@bridebook/toolbox/src/datepicker/types';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import type { IDatePicker } from '@bridebook/models/source/models/Weddings.types';
import { BudgetTimeOfYear, BudgetWeekDay } from './constants';
import type { IBudgetInitFormFields } from './types';
import type { IBudgetInitial } from './Costs.utils';
import {
  getGuestsValue,
  getTimeOfWeekValue,
  getTimeOfYearValue,
  getYearValue,
  mapSeasonToTimeOfYear,
  mapWeekDayToBudgetWeekDay,
} from './utils';

/** The form fields the wedding profile can answer on the couple's behalf. */
export type DerivedBudgetFields = Pick<
  IBudgetInitFormFields,
  'budget' | 'guestsEstimate' | 'timeOfWeek' | 'timeOfYear' | 'year'
>;

/**
 * Reads the budget-onboarding answers the wedding profile already holds: day of
 * the week and season from the date, guest bucket from the estimate, and the
 * target. Extracted so the budget page's onboarding and the planner's projection
 * derive them the same way rather than twice.
 */
export const deriveBudgetInitFields = (
  {
    date,
    guestsEstimate,
    budget,
  }: {
    date: IDatePicker | undefined;
    guestsEstimate: number | null | undefined;
    budget: number | null | undefined;
  },
  market: Market,
): DerivedBudgetFields => {
  const dateUI = mapDateToUI(date);
  const { month, year, dayUndecided, weekDay, season } = dateUI || ({} as IDatePickerUI);

  let budgetWeekDay: string | null = null;
  if (weekDay) {
    budgetWeekDay = mapWeekDayToBudgetWeekDay(weekDay);
  }
  if (!budgetWeekDay && !dayUndecided && dateUI) {
    const exactDate = mapToExactDate(dateUI, market);
    if (exactDate) {
      budgetWeekDay = getTimeOfWeekValue(new Date(exactDate).getDay());
    }
  }

  let budgetSeason: string;
  if (season) {
    budgetSeason = mapSeasonToTimeOfYear(season);
  } else if (month) {
    budgetSeason = getTimeOfYearValue(Number.parseInt(month, 10) + 1);
  } else {
    budgetSeason = BudgetTimeOfYear.undecided;
  }

  return {
    budget: Number(budget) || 0,
    guestsEstimate: getGuestsValue(Number(guestsEstimate || 0)),
    timeOfWeek: budgetWeekDay || BudgetWeekDay.undecided,
    // Markets with no directory have no seasonal pricing to key off.
    timeOfYear: market.hasAnyDirectory ? budgetSeason : '',
    year: year ? getYearValue(Number.parseInt(year, 10)) : 'undecided',
  };
};

const DAY_MAP: Record<string, IBudgetInitial['day']> = {
  undecided: 'sat',
  midweek: 'midweek',
  fri: 'fri-sun',
  sun: 'fri-sun',
  sat: 'sat',
};

/**
 * The form's answers as `processCosts` wants them. Undecided values fall back to
 * the commonest case — a Saturday in peak season, 99 guests, £15,000 — which is
 * what makes an allocation computable before the couple has decided anything.
 */
export const prepareInitProps = (
  fields: Pick<IBudgetInitFormFields, 'budget' | 'guestsEstimate' | 'timeOfWeek' | 'timeOfYear'> & {
    optionalCategories: IBudgetInitFormFields['optionalCategories'];
  },
  splitBudget: boolean,
): IBudgetInitial => ({
  estimate: Number(fields.budget || 15000),
  day: DAY_MAP[fields.timeOfWeek],
  season: fields.timeOfYear === 'off-season' ? 'off-season' : 'season',
  guests: fields.guestsEstimate === 'undecided' ? 99 : Number(fields.guestsEstimate),
  // A category the couple left unticked is one they don't want costing.
  excludeCategories: Object.keys(filter((wanted) => !wanted, fields.optionalCategories)),
  split: splitBudget,
});
